import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getReportesDashboard, getIngresosMensuales, getTopClientes, getClientes } from '../services/api';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style:'currency', currency:'COP', minimumFractionDigits:0 }).format(n || 0);
// Formato compacto en convención colombiana: M = millones, "mil" = miles.
// (NO se usa "B"/billón: en Colombia billón = millón de millones = 10¹², lo que
//  confundiría; mil millones se muestran como "1.057,3 M".)
const fmtK = (n) => {
  n = Number(n || 0);
  if (n >= 1e6) return '$' + (n / 1e6).toLocaleString('es-CO', { maximumFractionDigits: 1 }) + ' M';
  if (n >= 1e3) return '$' + (n / 1e3).toLocaleString('es-CO', { maximumFractionDigits: 0 }) + ' mil';
  return fmt(n);
};
const MESES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
const mesLabel = (iso) => { try { const d = new Date(iso); return `${MESES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`; } catch { return ''; } };

export default function Dashboard() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [ingresos, setIngresos] = useState([]);
  const [top, setTop] = useState([]);
  const [nClientes, setNClientes] = useState(0);

  const anioActual = new Date().getFullYear();
  useEffect(() => {
    getReportesDashboard(anioActual).then(r => setDash(r.data)).catch(() => {});
    getIngresosMensuales().then(r => setIngresos(r.data || [])).catch(() => {});
    getTopClientes().then(r => setTop(r.data || [])).catch(() => {});
    getClientes().then(r => setNClientes(r.data.length)).catch(() => {});
  }, []);

  const fac = dash?.facturacion || {};
  const cotTotal = (dash?.cotizaciones || []).reduce((s, c) => s + Number(c.total || c.monto || 0), 0);
  const proyActivos = (dash?.proyectos || []).find(p => p.estado === 'en_progreso')?.total || 0;

  const kpis = [
    { label:'Total facturado',   value:fmtK(fac.total_facturado),          color:'#10b981', icon:'💰', path:'/facturacion' },
    { label:'Cartera pendiente', value:fmtK(fac.saldo_pendiente),          color:'#f59e0b', icon:'🧾', path:'/cuentas-cobrar' },
    { label:'Compras',           value:fmtK(dash?.compras?.total_compras), color:'#3b82f6', icon:'🛒', path:'/compras' },
    { label:'Clientes',          value:nClientes,                           color:'#06b6d4', icon:'👥', path:'/clientes' },
    { label:'Cotizaciones',      value:(dash?.cotizaciones || []).reduce((s,c)=>s+Number(c.total||0),0), color:'#8b5cf6', icon:'📋', path:'/cotizaciones' },
    { label:'Proyectos activos', value:proyActivos,                         color:'#f97316', icon:'⚡', path:'/proyectos' },
  ];

  const maxIng = Math.max(1, ...ingresos.map(m => Number(m.total_facturado || 0)));
  const pendientes = dash?.facturasPendientes || [];

  return (
    <div>
      <h1 style={s.h1}>Dashboard</h1>
      <p style={s.sub}>Comportamiento del año {anioActual}</p>

      <div style={s.kpiGrid}>
        {kpis.map(k => (
          <div key={k.label} style={{ ...s.kpi, borderTop:`3px solid ${k.color}` }} onClick={() => navigate(k.path)}>
            <div style={s.kpiTop}><span style={{ fontSize:22 }}>{k.icon}</span>
              <span style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</span></div>
            <div style={s.kpiLabel}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Ingresos mensuales */}
      <div style={s.card}>
        <div style={s.cardHead}><span style={s.cardTitle}>📈 Ingresos mensuales</span>
          <span style={s.legend}><i style={{ ...s.dot, background:'#3b82f6' }} /> Facturado &nbsp; <i style={{ ...s.dot, background:'#10b981' }} /> Cobrado</span>
        </div>
        {ingresos.length === 0 ? <p style={s.empty}>Aún no hay facturación registrada.</p> : (
          <div style={s.chart}>
            {ingresos.slice(-12).map((m, i) => (
              <div key={i} style={s.barCol} title={`${mesLabel(m.mes)} · Facturado ${fmt(m.total_facturado)} · Cobrado ${fmt(m.total_cobrado)}`}>
                <div style={s.barVal}>{fmtK(m.total_facturado)}</div>
                <div style={s.barPair}>
                  <div style={{ ...s.bar, height:Math.round(Number(m.total_facturado||0)/maxIng*140), background:'#3b82f6' }} />
                  <div style={{ ...s.bar, height:Math.round(Number(m.total_cobrado||0)/maxIng*140), background:'#10b981' }} />
                </div>
                <div style={s.barLbl}>{mesLabel(m.mes)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top clientes + Cartera */}
      <div style={s.grid2}>
        <div style={s.card}>
          <div style={s.cardHead}><span style={s.cardTitle}>🏆 Top clientes</span></div>
          <table style={s.table}><tbody>
            {top.slice(0, 8).map((c, i) => (
              <tr key={i} style={s.tr}>
                <td style={{ ...s.td, color:'#f1f5f9' }}>{i + 1}. {c.razon_social}</td>
                <td style={{ ...s.td, textAlign:'right', color:'#64748b' }}>{c.facturas} fact.</td>
                <td style={{ ...s.td, textAlign:'right', color:'#10b981', fontWeight:600 }}>{fmtK(c.total_facturado)}</td>
              </tr>
            ))}
            {top.length === 0 && <tr><td style={{ ...s.td, color:'#64748b' }}>Sin datos de facturación.</td></tr>}
          </tbody></table>
        </div>

        <div style={s.card}>
          <div style={s.cardHead}><span style={s.cardTitle}>🧾 Facturas por cobrar</span>
            <button style={s.link} onClick={() => navigate('/cuentas-cobrar')}>Ver todo →</button></div>
          <table style={s.table}><tbody>
            {pendientes.slice(0, 8).map((f, i) => (
              <tr key={i} style={s.tr}>
                <td style={{ ...s.td, color:'#93c5fd' }}>{f.numero}</td>
                <td style={{ ...s.td, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.cliente}</td>
                <td style={{ ...s.td, textAlign:'right', color:'#f59e0b', fontWeight:600 }}>{fmtK(f.saldo)}</td>
              </tr>
            ))}
            {pendientes.length === 0 && <tr><td style={{ ...s.td, color:'#64748b' }}>Sin cartera pendiente 🎉</td></tr>}
          </tbody></table>
        </div>
      </div>
    </div>
  );
}

const s = {
  h1: { fontSize:24, fontWeight:700, color:'#f1f5f9', margin:'0 0 2px' },
  sub: { color:'#64748b', fontSize:14, margin:'0 0 20px' },
  kpiGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:14, marginBottom:20 },
  kpi: { background:'#1e293b', border:'1px solid #334155', borderRadius:12, padding:'16px 18px', cursor:'pointer', display:'flex', flexDirection:'column', gap:6 },
  kpiTop: { display:'flex', justifyContent:'space-between', alignItems:'center' },
  kpiLabel: { color:'#64748b', fontSize:12 },
  card: { background:'#1e293b', border:'1px solid #334155', borderRadius:12, padding:20, marginBottom:16 },
  cardHead: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 },
  cardTitle: { color:'#f1f5f9', fontSize:15, fontWeight:600 },
  legend: { color:'#94a3b8', fontSize:12, display:'flex', alignItems:'center' },
  dot: { display:'inline-block', width:10, height:10, borderRadius:2, marginRight:4 },
  chart: { display:'flex', alignItems:'flex-end', gap:10, overflowX:'auto', paddingTop:10, minHeight:190 },
  barCol: { display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:46, flex:1 },
  barVal: { color:'#64748b', fontSize:10, whiteSpace:'nowrap' },
  barPair: { display:'flex', alignItems:'flex-end', gap:3, height:140 },
  bar: { width:12, borderRadius:'3px 3px 0 0', minHeight:2, transition:'height .3s' },
  barLbl: { color:'#94a3b8', fontSize:11 },
  grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 },
  table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
  tr: { borderTop:'1px solid #263244' },
  td: { padding:'6px 8px', color:'#94a3b8' },
  link: { background:'none', border:'none', color:'#7dd3fc', cursor:'pointer', fontSize:12 },
  empty: { color:'#64748b', fontSize:13, textAlign:'center', padding:'20px 0' },
};
